<template>
  <template v-if="showData">
    <div id="app">
      <RouterView />
    </div>
  </template>
  <template v-else>
    <div class="flex flex-col items-center justify-center h-screen" :estado="showData">
      <div class="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-32 w-32 mb-4"></div>
      <h2 class="text-center text-xl font-semibold">Cargando...</h2>
    </div>
  </template>

  <!--
  <VaModal
    v-model="showLoadingScreen"
    :title="loadingText"
    message="Classic modal overlay which represents a dialog box or other interactive component, such as a dismissible alert, sub-window, etc."
    ok-text="Apply"
    blur
  >
    <VaInnerLoading
      loading
      :size="60"
    ></VaInnerLoading>
    <template #default>
      <div class="flex flex-col items-center justify-center p-4">
        <div class="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-32 w-32 mb-4"></div>
        <h2 class="text-center text-xl font-semibold">{{ loadingText }}</h2>
      </div>
    </template>
  </VaModal>

  -->
</template>
<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, ref, Ref } from 'vue'
import { useColors } from 'vuestic-ui'

import { NavigationGuardNext, RouteLocationNormalized, RouterView, useRoute, useRouter } from 'vue-router'

import tokenManager from './managers/identity/TokenManager'
import identityManager, { LoggedInUser } from './managers/identity/IdentityManager'
import roleCapacitiesManager, { UserRole } from './managers/identity/RoleCapacitiesManager'
import UsersServices from './services/api-services/UsersServices'
import dataStorageManager, { BucketDefaultIDs } from './managers/data/DataStorageManager'
import ArmadoresServices from './services/api-services/ArmadoresServices'
import ProveedoresServices from './services/api-services/ProveedoresServices'
import dataQueryManager from './managers/network/DataQueryManager'
import DocumentsServices from './services/api-services/DocumentsServices'
import RequestsServices from './services/api-services/RequestsServices'
import EmbarcacionesServices from './services/api-services/EmbarcacionesServices'
import ChangesServices from './services/api-services/ChangesServices'
import TripulantesServices from './services/api-services/TripulantesServices'
import ViajesServices from './services/api-services/ViajesServices'
import whatsappNotificationManager from './managers/network/WANotificationsServices'
import emailNotificationManager from './managers/network/EmailNotificationsServices'
import BitacoraServices from './services/api-services/BitacoraServices'
import TarifasServices from './services/api-services/TarifasServices'
import CobrosServices from './services/api-services/CobrosServices'
import ServicesServices from './services/api-services/ServicesServices'
import ServiceVehiclesServices from './services/api-services/ServiceVehiclesServices'
import NotificacionesServices from './services/api-services/NotificationsServices'
import navigatorDataManager from './managers/data/NavigatorDataManager'
import dataHubManager, { DataHubOperationType } from './managers/data/DataHubManager'
import { watch } from 'vue'
import { se } from 'date-fns/locale'
import dataHubServices from './services/data-hub-services/DataHubServices'
import { armadoresHubName, ArmadoresHubPipelines } from './services/data-hub-services/ArmadoresHub'
import { embarcacionesHubName } from './services/data-hub-services/EmbarcacionesHub'
import { representantesHubName } from './services/data-hub-services/RepresentantesHub'
import RepresentantesServices from './services/api-services/RepresentantesServices'
import InterinosServices from './services/api-services/InterinosServices'
import HorariosServices from './services/api-services/HorariosServices'
import EsporadicosServices from './services/api-services/EsporadicosServices'

const { setColors } = useColors()

const notificacionesServices = new NotificacionesServices()
const userServices = new UsersServices()
const requestsServices = new RequestsServices()
const changesServices = new ChangesServices()
const armadoresServices = new ArmadoresServices()
const embarcacionesServices = new EmbarcacionesServices()
const proveedoresServices = new ProveedoresServices()
const tripulantesServices = new TripulantesServices()
const viajesServices = new ViajesServices()
const servicesServices = new ServicesServices()
const serviceVehiclesServices = new ServiceVehiclesServices()
const documentsServices = new DocumentsServices()
const bitacoraServices = new BitacoraServices()
const tarifasServices = new TarifasServices()
const cobrosServices = new CobrosServices()
const representantesServices = new RepresentantesServices()
const rolesAlternativosServices = new InterinosServices()
const horariosServices = new HorariosServices()
const esporadicosServices = new EsporadicosServices()

const roleLoaded: Ref<boolean> = ref(false)

const servicesStartupComplete: Ref<boolean> = ref(false)
const managersStartupComplete: Ref<boolean> = ref(false)
const dataHubStartupComplete: Ref<boolean> = ref(false)

const showData: Ref<boolean> = computed(() => {
  return roleLoaded.value && servicesStartupComplete.value && managersStartupComplete.value
})

const showLoadingScreen: Ref<boolean> = ref(true)
const loadingText: Ref<string> = ref('Cargando...')

// Rutas públicas (accesibles sin sesión). 'set-password' es el selector de contraseña por token
// (observación #8): el apoderado/usuario nuevo o que recupera su clave llega SIN sesión, así que
// debe estar en la whitelist o identityCheck lo redirige a /login y el flujo de token queda inutilizable.
// 'verificar-ao' permite la comprobación pública de autenticidad y vigencia de la AO vía escaneo de QR.
const notRoleAllowedRoutes = ['login', 'signup', 'recover-password', 'recover-password-email', 'set-password', 'verificar-ao']
const loggedInUser: Ref<LoggedInUser | null> = ref(null)
const router = useRouter()

onBeforeMount(() => {
  setColors({
    //primary: '#0175bf',
    primary: '#007cc3',
  })
  managersStartup()
  servicesStartup()
})

onMounted(() => {})

async function managersStartup() {
  await navigatorDataManager.startup()
  tokenManager.startup()
  dataQueryManager.startup()
  dataHubManager.startup()
  identityManager.startup()
  identityManager.subscribe((user) => {
    loggedInUser.value = user
  })
  roleCapacitiesManager.startup()
  redirectOutlogged()
  roleCapacitiesManager.subscribe(() => {
    redirectOutlogged()
  })
  if (import.meta.env.DEV) {
    ;(window as any).dataStorageManager = dataStorageManager
    ;(window as any).identityManager = identityManager
  }
  whatsappNotificationManager.startup()
  emailNotificationManager.startup()

  managersStartupComplete.value = true
}

function servicesStartup() {
  notificacionesServices.startup()
  userServices.startup()
  requestsServices.startup()
  changesServices.startup()
  armadoresServices.startup()
  embarcacionesServices.startup()
  proveedoresServices.startup()
  tripulantesServices.startup()
  viajesServices.startup()
  servicesServices.startup()
  serviceVehiclesServices.startup()
  documentsServices.startup()
  bitacoraServices.startup()
  tarifasServices.startup()
  cobrosServices.startup()
  representantesServices.startup()
  rolesAlternativosServices.startup()
  horariosServices.startup()
  esporadicosServices.startup()

  servicesStartupComplete.value = true
}

watch([managersStartupComplete, servicesStartupComplete, dataHubStartupComplete], (newVal) => {
  console.log('Managers startup complete:', managersStartupComplete.value)
  console.log('Services startup complete:', servicesStartupComplete.value)
  if (managersStartupComplete.value && !dataHubStartupComplete.value) {
    console.log('Managers startup is complete.')
    dataHubServices.startup()
    dataHubStartupComplete.value = true
  }

  if (managersStartupComplete.value && servicesStartupComplete.value && dataHubStartupComplete.value) {
    console.log('All startup processes complete. Application is ready.')
    dataHubTests()
  }
})

async function redirectOutlogged() {
  await identityManager.loadPromise
  await roleCapacitiesManager.loadPromise
  const userRole = roleCapacitiesManager.getRoleDetails()
  if (!userRole) {
    roleLoaded.value = false
    // pathname (no href): href incluye el query string, así que rutas públicas con params
    // (ej. set-password?token=…) nunca matcheaban la whitelist y se redirigía a /login.
    const route = window.location.pathname.split('/').pop()
    if (!notRoleAllowedRoutes.includes(route as string)) {
      router.push({ name: 'login', query: { to: window.location.pathname + window.location.search } })
    } else {
      roleLoaded.value = true
    }
  } else {
    roleLoaded.value = true
  }
}

router.beforeEach((to, from, next) => {
  identityCheck(to, next)
})

async function identityCheck(to: RouteLocationNormalized, next: NavigationGuardNext) {
  await identityManager.loadPromise
  const user = identityManager.getUser()

  if (user === null) {
    if (notRoleAllowedRoutes.includes(to.name as string)) {
      roleLoaded.value = true
      next()
    } else {
      next({ name: 'login', query: { to: to.fullPath } })
    }
  } else {
    next()
  }
}

function dataHubTests() {
  console.log('Starting Data Hub tests...')
  dataHubManager.subscribeStorage(representantesHubName, 'testRepresentantesHubSubscriber', (data) => {
    console.log('Received data from RepresentantesHub:', data)
  })
  const data = dataHubServices.getHub(representantesHubName)?.storageOperations.getData()
  console.log('Initial data from RepresentantesHub:', data)
}

async function dataLoadTest() {
  //const users = await dataStorageManager.subscribeBucket(BucketDefaultIDs.USERS, showDataText);
  //const armadores = await dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, showDataText);
  dataStorageManager.subscribeBucket(BucketDefaultIDs.PROVEEDORES, showDataText)
}

async function dataUploadTest() {
  const newArmador = {
    tipo_persona: 'natural',
    razon_social: 'Test Razon Social 2',
    rut_empresa: '21222333-4',
    nombre_representante: 'Test Nombre Representante 2',
    rut_representante: '22123123-4',
    telefono_representante: '12345678',
    email_representante: 'test223@example.com',
    imagen_perfil: 'Image URL 2',
    usuario_creacion: 1,
  }

  const result = await dataStorageManager.performOperation(BucketDefaultIDs.ARMADORES, 'insertArmador', newArmador)
}

function showDataText(dataList: Array<any>) {
  console.log('Data list:', dataList)
}

;(window as any).dataLoadTest = dataLoadTest
;(window as any).dataUploadTest = dataUploadTest
;(window as any).testNotification = () => {
  whatsappNotificationManager.callNotification(45, 50207581, 'Prueba de funcion')
}
</script>
<style lang="scss">
#app {
  font-family: 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

body {
  margin: 0;
  min-width: 20rem;
}

@font-face {
  font-family: 'Century Gothic';
  src: url('/centurygothic.ttf') format('truetype');
}
</style>
